// src/SchedulerApp.js
import React, { useState, useEffect } from "react";
import { apiFetch } from "./utils/apiUtils";
import {
  Container,
  Box,
  Tabs,
  Tab,
  Button,
  Typography,
  TextField,
  Snackbar,
  Alert,
} from "@mui/material";
import CustomGrid from "./components/CustomGrid";
import GroomerManagement from "./components/GroomerManagement";
import VanManagement from "./components/VanManagement";
import Utilities from "./Utilities";
import Reports from "./Reports";
import ModelingGrid from "./components/ModelingGrid";
import AddDaysDialog from "./AddDaysDialog";

const SchedulerApp = () => {
  const [scheduleData, setScheduleData] = useState([]);
  // Six tabs: 0 = Master Schedule, 1 = Groomers, 2 = Vans, 3 = Utilities, 4 = Reports, 5 = Schedule Modeling
  const [tabValue, setTabValue] = useState(0);
  const [addDaysOpen, setAddDaysOpen] = useState(false);
  const [dateFilter, setDateFilter] = useState(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today.toISOString().split("T")[0];
  });
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  // Fetch schedule data from the API
  const fetchScheduleData = (token) => {
    // Using apiFetch which automatically handles authorization headers and URL resolution
    apiFetch(`/api/fleet/schedule`)
      .then((res) => {
        if (!res.ok) throw new Error("Error fetching schedule data");
        return res.json();
      })
      .then((data) => {
        console.log("Fetched schedule data:", data);
        setScheduleData(data);
      })
      .catch((err) => console.error(err));
  };

  // On component mount, fetch schedule data if token exists.
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      fetchScheduleData(token);
    }
  }, []);

  const handleGridChange = (newGrid) => {
    console.log("Updated grid data:", newGrid);
    setScheduleData(newGrid.flat());
  };

  const handleSaveSchedule = (grid) => {
    // Using apiFetch which automatically handles authorization headers and URL resolution
    apiFetch(`/api/fleet/schedule`, {
      method: "PUT",
      body: JSON.stringify(grid.flat()),
    })
      .then((res) => {
        if (!res.ok) throw new Error("Failed to save schedule");
        return res.json();
      })
      .then((data) => {
        console.log("Schedule saved successfully:", data);
        fetchScheduleData(localStorage.getItem("token"));
      })
      .catch((err) => console.error(err));
  };

  const handleAddDays = (newEndDate) => {
    // Using apiFetch which automatically handles authorization headers and URL resolution
    apiFetch(`/api/fleet/schedule/add-days`, {
      method: "POST",
      body: JSON.stringify({
        start_date: dateFilter,
        end_date: newEndDate,
      }),
    })
      .then((res) => {
        if (!res.ok) throw new Error("Failed to add days");
        return res.json();
      })
      .then((data) => {
        console.log("Days added successfully:", data);
        fetchScheduleData(localStorage.getItem("token"));
        setAddDaysOpen(false);
        // Show success message
        setSnackbarMessage("Days added successfully!");
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
      })
      .catch((err) => {
        console.error("Error adding days:", err);
        setSnackbarMessage("Error adding days: " + err.message);
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
        setAddDaysOpen(false);
      });
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 2 }}>
      <Typography variant="h4" sx={{ textAlign: "center", mb: 2 }}>
        ZG Groomer Scheduler Application
      </Typography>
      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          textColor="inherit"
          indicatorColor="primary"
          variant="fullWidth"
        >
          <Tab label="Master Schedule" />
          <Tab label="Groomers" />
          <Tab label="Vans" />
          <Tab label="Utilities" />
          <Tab label="Reports" />
          <Tab label="Schedule Modeling" />
        </Tabs>
      </Box>
      {tabValue === 0 && (
        <Box>
          <Box sx={{ mb: 2, textAlign: "right" }}>
            <Button variant="outlined" onClick={() => setAddDaysOpen(true)}>
              Add Days
            </Button>
          </Box>
          <Box sx={{ mb: 2, textAlign: "right" }}>
            <Typography variant="caption" sx={{ mr: 1 }}>
              Show dates from:
            </Typography>
            <TextField
              type="date"
              size="small"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Box>
          <CustomGrid
            gridData={scheduleData}
            onGridChange={handleGridChange}
            onSaveSchedule={handleSaveSchedule}
            numRows={6}
            dateFilter={dateFilter}
          />
        </Box>
      )}
      {tabValue === 1 && <GroomerManagement />}
      {tabValue === 2 && <VanManagement />}
      {tabValue === 3 && (
        <Box sx={{ p: 2 }}>
          <Typography variant="h6">Utilities</Typography>
          <Utilities 
            refreshScheduleData={() => fetchScheduleData(localStorage.getItem("token"))}
            currentLastDate={scheduleData.length > 0 
              ? scheduleData.reduce((max, cell) => 
                  cell.day > max ? cell.day : max, scheduleData[0].day) 
              : dateFilter}
          />
        </Box>
      )}
      {tabValue === 4 && <Reports />}
      {tabValue === 5 && (
        <Box sx={{ p: 2 }}>
          <ModelingGrid />
        </Box>
      )}
      <AddDaysDialog
        open={addDaysOpen}
        onClose={() => setAddDaysOpen(false)}
        onSubmit={handleAddDays}
        currentLastDate={dateFilter}
      />
      
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default SchedulerApp;
